import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar, Printer } from "lucide-react";
import { format, addDays, addWeeks, addMonths } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type CalendarViewType = "day" | "3day" | "week" | "month";

interface CalendarViewControlsProps {
  viewType: CalendarViewType;
  onViewChange: (view: CalendarViewType) => void;
  currentDate: Date;
  onNavigate: (direction: "prev" | "next") => void;
  onToday: () => void;
}

const getDateRangeLabel = (viewType: CalendarViewType, date: Date): string => {
  switch (viewType) {
    case "day":
      return format(date, "EEEE, MMMM d, yyyy");
    case "3day": {
      const start = addDays(date, -1);
      const end = addDays(date, 1);
      return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
    }
    case "week": {
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());
      const endOfWeek = addDays(startOfWeek, 6);
      return `${format(startOfWeek, "MMM d")} - ${format(endOfWeek, "MMM d, yyyy")}`;
    }
    case "month":
      return format(date, "MMMM yyyy");
  }
};

export function CalendarViewControls({
  viewType,
  onViewChange,
  currentDate,
  onNavigate,
  onToday,
}: CalendarViewControlsProps) {
  const isMobile = useIsMobile();
  
  const handlePrint = () => {
    window.print();
  };
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* View Type Selector */}
      <div className="flex items-center gap-1 border rounded-lg p-1">
        <Button
          variant={viewType === "day" ? "default" : "ghost"}
          size="sm"
          onClick={() => onViewChange("day")}
        >
          Day
        </Button>
        <Button
          variant={viewType === "3day" ? "default" : "ghost"}
          size="sm"
          onClick={() => onViewChange("3day")}
        >
          3 Days
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant={viewType === "week" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onViewChange("week")}
                  disabled={isMobile}
                  className={isMobile ? "opacity-50 cursor-not-allowed" : ""}
                >
                  Week
                </Button>
              </span>
            </TooltipTrigger>
            {isMobile && (
              <TooltipContent>
                <p>Week view is best on larger screens</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <Button
          variant={viewType === "month" ? "default" : "ghost"}
          size="sm"
          onClick={() => onViewChange("month")}
        >
          Month
        </Button>
      </div>

      {/* Date Range and Navigation */}
      <div className="flex items-center gap-2">
        <h2 className="text-base lg:text-lg font-semibold hidden sm:block whitespace-nowrap">
          {getDateRangeLabel(viewType, currentDate)}
        </h2>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate("prev")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday}>
            <Calendar className="h-4 w-4 mr-1" />
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate("next")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="ml-2">
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Date Range */}
      <h2 className="text-sm font-semibold sm:hidden whitespace-nowrap">
        {getDateRangeLabel(viewType, currentDate)}
      </h2>
    </div>
  );
}
